/**
 * Permission resolver — pure functions, the one permission logic.
 *
 * The client (`usePermissions` and its direct importers), `apps/functions`
 * (the callables that resolve a target's caps) and `@sendly/server`
 * (`gymAuthz.js` — `requireGymCap` and friends — and `requirePlatformCap`) all
 * run THIS module.
 *
 * `firestore.rules` does NOT. It checks role strings plus ad-hoc
 * `extraCaps.hasAny` terms; the generated `hasCap()` helper that used to mirror
 * this formula was removed with its codegen, because no rule called it. One
 * rule now spells the formula out by hand — `hasSeriesManageCap()`, for the
 * `series.manage` cap only — and it is a hand-written mirror, not a generated
 * one: a change to this resolver does not propagate there.
 */
import { GYM_ROLE_PRESETS, PLATFORM_ROLE_PRESETS } from './rolePresets.js';
import { ALL_GYM_CAPS, ADMIN_PANEL_SUB_CAPS } from './capabilities.js';

/**
 * Resolve effective platform capabilities from a user document.
 * @param {object|null} userDoc - users/{uid} document data
 * @returns {Set<string>} effective platform capabilities
 */
export function resolvePlatformCaps(userDoc) {
  if (!userDoc) return new Set();

  const caps = new Set();

  // 1. Platform role preset
  if (userDoc.platformRole && PLATFORM_ROLE_PRESETS[userDoc.platformRole]) {
    PLATFORM_ROLE_PRESETS[userDoc.platformRole].forEach(c => caps.add(c));
  }

  // 2. Extra platform caps
  (userDoc.platformCaps || []).forEach(c => caps.add(c));

  // 3. Revoked platform caps
  (userDoc.platformRevokedCaps || []).forEach(c => caps.delete(c));

  return caps;
}

/**
 * Does this user hold ANY platform capability? The one definition of "a
 * platform user": the /admin guard opens on it, `banUserPlatform` refuses such
 * a target, and every writer of the three platform fields stores it as
 * `users/{uid}.hasPlatformAccess` (true, or the field deleted) — the key the
 * "Platform adminok" list queries, since a resolved set cannot be queried.
 * @param {object|null} userDoc - users/{uid} document data (or just its three platform fields)
 */
export function holdsPlatformAccess(userDoc) {
  return resolvePlatformCaps(userDoc).size > 0;
}

/**
 * Does this user hold every capability of the `platformAdmin` preset? False for
 * a partial platform user (the "Részleges" badge) — whether the preset came
 * through the role or as single caps, and whether a cap was revoked from it.
 * The last-full-admin rule of the admin list and of `updateUserPlatformCaps`
 * counts these.
 * @param {object|null} userDoc - users/{uid} document data
 */
export function holdsFullPlatformPreset(userDoc) {
  const caps = resolvePlatformCaps(userDoc);
  return PLATFORM_ROLE_PRESETS.platformAdmin.every((c) => caps.has(c));
}

/**
 * Resolve effective gym capabilities from a member document and user document.
 * @param {object|null} memberDoc - gyms/{gymId}/members/{uid} document data
 * @param {object|null} userDoc - users/{uid} document data
 * @returns {Set<string>} effective gym capabilities
 */
export function resolveGymCaps(memberDoc, userDoc) {
  const platformCaps = resolvePlatformCaps(userDoc);

  // platform.gym.adminAny → every gym cap + derived
  if (platformCaps.has('platform.gym.adminAny')) {
    const caps = new Set(ALL_GYM_CAPS);
    caps.add('adminPanel.access');
    return caps;
  }

  if (!memberDoc) return new Set();

  // Role preset
  const preset = GYM_ROLE_PRESETS[memberDoc.role] || [];
  const caps = new Set(preset);

  // Extra caps
  (memberDoc.extraCaps || []).forEach(c => caps.add(c));

  // Revoked caps
  (memberDoc.revokedCaps || []).forEach(c => caps.delete(c));

  // Derived: adminPanel.access
  if (ADMIN_PANEL_SUB_CAPS.some(sub => caps.has(sub))) {
    caps.add('adminPanel.access');
  }

  return caps;
}

/**
 * The permission-audit diff between two grant states (`{ role, extraCaps, revokedCaps }`).
 * `role` is null when unchanged; each cap list reports what was added and removed.
 * @param {{role?: string, extraCaps?: string[], revokedCaps?: string[]}} before
 * @param {{role?: string, extraCaps?: string[], revokedCaps?: string[]}} after
 */
export function computeAuditChanges(before, after) {
  const changes = {
    role: null,
    extraCaps: { added: [], removed: [] },
    revokedCaps: { added: [], removed: [] },
  };
  if (before.role !== after.role) {
    changes.role = { from: before.role || null, to: after.role };
  }
  const beforeExtra = new Set(before.extraCaps || []);
  const afterExtra = new Set(after.extraCaps || []);
  changes.extraCaps.added = [...afterExtra].filter(c => !beforeExtra.has(c));
  changes.extraCaps.removed = [...beforeExtra].filter(c => !afterExtra.has(c));
  const beforeRevoked = new Set(before.revokedCaps || []);
  const afterRevoked = new Set(after.revokedCaps || []);
  changes.revokedCaps.added = [...afterRevoked].filter(c => !beforeRevoked.has(c));
  changes.revokedCaps.removed = [...beforeRevoked].filter(c => !afterRevoked.has(c));
  return changes;
}

/** True when `computeAuditChanges` found nothing to record (a no-op save). */
export function isEmptyChanges(changes) {
  return !changes.role
    && changes.extraCaps.added.length === 0
    && changes.extraCaps.removed.length === 0
    && changes.revokedCaps.added.length === 0
    && changes.revokedCaps.removed.length === 0;
}
